import { Component, OnInit, Optional, ViewChild } from '@angular/core';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { Book } from '../../models/book.model';
import { EventBusService } from '../../services/event-bus.service';

import { TabItem } from '../tab/tab.component';
import { RightPanelComponent } from '../right-panel/right-panel.component';
import { AnnotationListComponent } from '../annotation-list/annotation-list.component';
import { UIStateService } from '../../services/ui-state.service';
import { PDFDrawingService } from '../../services/pdf-drawing.service';
import { BookService } from '../../services/book.service';
import { ReadingGroupsService } from '../../services/reading-groups.service';

enum Tab {
  highlight = '하이라이트',
  memo = '메모',
  bookmark = '북마크',
  drawing = '필기',
}

@Component({
  selector: 'viewer-annotations',
  templateUrl: './annotations.component.html',
  styleUrl: './annotations.component.scss',
})
export class AnnotationsComponent implements OnInit {
  @ViewChild('annotationList')
  annotationListComp?: AnnotationListComponent<unknown>;
  @ViewChild('drawingList') drawingListComp?: AnnotationListComponent<unknown>;

  private _unsubscriber = new Subject<void>();

  public _tabItems: TabItem[] = [
    {
      icon: '하이라이트',
      name: Tab.highlight,
    },
    {
      icon: '메모',
      iconType: 'solid',
      name: Tab.memo,
    },
    {
      icon: '북마크',
      iconType: 'solid',
      name: Tab.bookmark,
    },
  ];

  public _Tab = Tab;
  public _selectedTab = Tab.highlight;

  public _book?: Book;

  constructor(
    private _rightPanelComponent: RightPanelComponent,
    private _eventBusService: EventBusService,
    private _uiStateService: UIStateService,
    @Optional() private _pdfDrawingService: PDFDrawingService,
    private _bookService: BookService,
    private _readingGroupsService: ReadingGroupsService
  ) {}

  ngOnInit(): void {
    if (this._pdfDrawingService) {
      this._tabItems.push({
        icon: '펜',
        iconType: 'solid',
        name: Tab.drawing,
      });
    }

    this._eventBusService
      .on('RightPanelComponent:opened')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ activePanel }) => {
        if (activePanel === 'myAnnotations') {
          this._selectedTab = Tab.memo;
        }
      });

    this._eventBusService
      .on('ContentsComponent:bookLoad')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        if (this._uiStateService.isCommentaryEditor) {
          this._tabItems.pop();
        }
        this._book = data.book;
      });

    this._eventBusService
      .on('AnnotationSettingsFabComponent:anotationsButtonClick')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._rightPanelComponent.open('myAnnotations');
        this.annotationListComp?.loadItems();
        this.drawingListComp?.loadItems();
      });

    // 나의책 모드에서 초기 진입 시 right-panel 자동 열기
    combineLatest([
      this._bookService.book$,
      this._readingGroupsService.currentGroup$,
    ])
      .pipe(
        filter(([book, group]) => book !== null && group === null), // 책은 있고 그룹은 없을 때 (나의책 모드)
        first(),
        takeUntil(this._unsubscriber)
      )
      .subscribe(() => {
        this._rightPanelComponent.open('myAnnotations');
        this.annotationListComp?.loadItems();
        this.drawingListComp?.loadItems();
      });
  }

  _onTabChange(): void {
    return;
  }

  _onCloseButtonClick(): void {
    this._rightPanelComponent.close();
  }

  _onItemClicked(
    url: string,
    source: 'highlight' | 'memo' | 'bookmark' | 'drawing'
  ): void {
    this._eventBusService.fire('AnnotationsComponent:itemClick', {
      url,
      source,
    });
  }
}
